// components/MenuCsvSection.jsx
import React, { useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DescriptionIcon from "@mui/icons-material/Description";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ApiServices from "../../../../../Utils/ApiServices/ApiServices";

const CSV_COLUMNS = [
  "food_type",
  "food_type_is_veg",
  "food_type_order",
  "category",
  "category_order",
  "item_name",
  "item_type",
  "item_description",
  "item_is_veg",
  "item_is_available",
  "item_badge",
  "item_prep_time",
  "item_calories",
  "item_order",
  "item_pricing_pickup",
  "item_pricing_delivery",
  "item_pricing_dine_in",
  "variant_group",
  "variant_name",
  "variant_pricing_pickup",
  "variant_pricing_delivery",
  "variant_pricing_dine_in",
  "variant_is_available",
  "variant_badge",
  "variant_order",
];

const SAMPLE_ROWS = [
  "Beverages,true,1,Cold Drinks,1,Mango Juice,item,Fresh mango juice,true,true,New,5,120,1,60,70,65,,,,,,,,",
  "Non-Veg,false,2,Mains,1,Chicken Masala,item,Rich creamy curry,false,true,Chef's Special,25,400,1,,,,,Half,120,140,130,true,,1",
  "Non-Veg,false,2,Mains,1,Chicken Masala,item,Rich creamy curry,false,true,Chef's Special,25,400,1,,,,,Full,220,250,230,true,,2",
  "Non-Veg,false,2,Mains,1,Fish Curry,item,Spicy fish curry,false,true,,30,350,2,,,,Size,Regular,180,200,190,true,,1",
  "Non-Veg,false,2,Mains,1,Fish Curry,item,Spicy fish curry,false,true,,30,350,2,,,,Size,Large,280,310,290,true,,2",
];

const MenuCsvSection = ({
  token,
  clientId,
  selectedLocation,
  locations = [],
  selectedLocationId,
  onSelectLocation,
  showSnackbar,
}) => {
  const fileInputRef = useRef(null);

  const [selectedFile, setSelectedFile] = useState(null);
  const [downloadingSample, setDownloadingSample] = useState(false);
  const [uploadingCsv, setUploadingCsv] = useState(false);

  const expectedColumnsText = useMemo(() => CSV_COLUMNS.join(","), []);

  const samplePreviewText = useMemo(() => {
    return [expectedColumnsText, ...SAMPLE_ROWS].join("\n");
  }, [expectedColumnsText]);

  const getErrorMessage = async (error, fallbackMessage) => {
    try {
      const responseData = error?.response?.data;

      if (!responseData) return fallbackMessage;

      if (typeof responseData === "string") {
        return responseData || fallbackMessage;
      }

      if (responseData instanceof Blob) {
        const text = await responseData.text();
        return text || fallbackMessage;
      }

      if (responseData?.detail) return responseData.detail;
      if (responseData?.message) return responseData.message;
      if (responseData?.error) return responseData.error;

      return fallbackMessage;
    } catch {
      return fallbackMessage;
    }
  };

  const handleChooseFile = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];

    if (!file) {
      setSelectedFile(null);
      return;
    }

    const fileName = file.name.toLowerCase();
    const isCsv =
      file.type === "text/csv" ||
      file.type === "application/vnd.ms-excel" ||
      fileName.endsWith(".csv");

    if (!isCsv) {
      setSelectedFile(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      showSnackbar("Please select a valid CSV file", "error");
      return;
    }

    setSelectedFile(file);
  };

  const buildCsvBlobFromResponse = async (response) => {
    const responseData = response?.data ?? response;

    if (responseData instanceof Blob) {
      return responseData;
    }

    if (typeof responseData === "string") {
      return new Blob([responseData], {
        type: "text/csv;charset=utf-8;",
      });
    }

    if (responseData?.csv && typeof responseData.csv === "string") {
      return new Blob([responseData.csv], {
        type: "text/csv;charset=utf-8;",
      });
    }

    if (responseData?.content && typeof responseData.content === "string") {
      return new Blob([responseData.content], {
        type: "text/csv;charset=utf-8;",
      });
    }

    if (Array.isArray(responseData)) {
      const text = responseData.join("\n");
      return new Blob([text], {
        type: "text/csv;charset=utf-8;",
      });
    }

    return new Blob([samplePreviewText], {
      type: "text/csv;charset=utf-8;",
    });
  };

  const handleDownloadSampleCsv = async () => {
    if (!selectedLocationId) {
      showSnackbar("Please select a location first", "error");
      return;
    }

    setDownloadingSample(true);

    try {
      const response = await ApiServices.get(
        `/api/v1/menu/${clientId}/locations/${selectedLocationId}/bulk-upload/`,
        {
          Authorization: `Bearer ${token}`,
          responseType: "blob",
        }
      );

      let csvBlob = await buildCsvBlobFromResponse(response);

      if (!(csvBlob instanceof Blob) || csvBlob.size === 0) {
        csvBlob = new Blob([samplePreviewText], {
          type: "text/csv;charset=utf-8;",
        });
      }

      const fileName = `${
        selectedLocation?.name
          ? selectedLocation.name.replace(/[^a-z0-9-_ ]/gi, "").replace(/\s+/g, "_")
          : "menu"
      }_sample_template.csv`;

      const downloadUrl = window.URL.createObjectURL(csvBlob);
      const link = document.createElement("a");
      link.href = downloadUrl;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(downloadUrl);

      showSnackbar("Sample CSV downloaded successfully", "success");
    } catch (error) {
      const message = await getErrorMessage(
        error,
        "Failed to download sample CSV"
      );
      showSnackbar(message, "error");
    } finally {
      setDownloadingSample(false);
    }
  };

  const handleUploadCsv = async () => {
    if (!selectedLocationId) {
      showSnackbar("Please select a location first", "error");
      return;
    }

    if (!selectedFile) {
      showSnackbar("Please choose a CSV file first", "error");
      return;
    }

    const formData = new FormData();
    formData.append("file", selectedFile);

    setUploadingCsv(true);

    try {
      await ApiServices.post(
        `/api/v1/menu/${clientId}/locations/${selectedLocationId}/bulk-upload/`,
        formData,
        {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data",
        }
      );

      showSnackbar("Menu CSV uploaded successfully", "success");
      setSelectedFile(null);

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const message = await getErrorMessage(error, "Failed to upload menu CSV");
      showSnackbar(message, "error");
    } finally {
      setUploadingCsv(false);
    }
  };

  return (
    <Box>
      <Stack
        direction={{ xs: "column", md: "row" }}
        justifyContent="space-between"
        spacing={2}
        mb={2}
      >
        <Box>
          <Typography variant="h6" fontWeight={700}>
            Menu CSV
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {selectedLocation
              ? `Download sample CSV and upload filled menu CSV for ${selectedLocation.name}`
              : "Select a location to download and upload menu CSV"}
          </Typography>
        </Box>

        <TextField
          select
          size="small"
          label="Selected Location"
          value={selectedLocationId || ""}
          onChange={(e) => onSelectLocation(e.target.value)}
          sx={{ minWidth: { xs: "100%", md: 260 } }}
        >
          {locations.map((loc) => (
            <MenuItem key={loc.id} value={loc.id}>
              {loc.name}
            </MenuItem>
          ))}
        </TextField>
      </Stack>

      <Divider sx={{ mb: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <DownloadIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Download Sample CSV
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Download the sample CSV template for the selected location.
                  Fill menu data in the same structure and upload it back.
                </Typography>

                <Alert severity="info" icon={<InfoOutlinedIcon fontSize="inherit" />}>
                  GET API returns CSV-style data. Keep the same column sequence and
                  do not rename headers.
                </Alert>

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: "action.hover",
                  }}
                >
                  <Typography variant="subtitle2" fontWeight={700} mb={1}>
                    Expected CSV Columns
                  </Typography>

                  <Box
                    sx={{
                      maxHeight: 180,
                      overflow: "auto",
                      p: 1.5,
                      borderRadius: 2,
                      backgroundColor: "#fff",
                      border: "1px solid",
                      borderColor: "divider",
                    }}
                  >
                    <Typography
                      variant="caption"
                      component="pre"
                      sx={{
                        m: 0,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        fontFamily: "monospace",
                      }}
                    >
                      {expectedColumnsText}
                    </Typography>
                  </Box>
                </Paper>

                <Box>
                  <Button
                    variant="contained"
                    startIcon={
                      downloadingSample ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <DownloadIcon />
                      )
                    }
                    onClick={handleDownloadSampleCsv}
                    disabled={!selectedLocationId || downloadingSample}
                  >
                    {downloadingSample ? "Downloading..." : "Download Sample CSV"}
                  </Button>
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={6}>
          <Card sx={{ borderRadius: 3, height: "100%" }}>
            <CardContent>
              <Stack spacing={2.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <UploadFileIcon color="primary" />
                  <Typography variant="subtitle1" fontWeight={700}>
                    Upload Filled CSV
                  </Typography>
                </Stack>

                <Typography variant="body2" color="text.secondary">
                  Choose the filled CSV file and upload it to create or update
                  menu records for the selected location.
                </Typography>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  style={{ display: "none" }}
                  onChange={handleFileChange}
                />

                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderRadius: 2,
                    minHeight: 92,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 2,
                    flexWrap: "wrap",
                    backgroundColor: selectedFile ? "success.lighter" : "action.hover",
                  }}
                >
                  <Stack direction="row" spacing={1} alignItems="center">
                    <DescriptionIcon color={selectedFile ? "success" : "action"} />
                    <Box>
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: "break-word", fontWeight: 600 }}
                      >
                        {selectedFile ? selectedFile.name : "No CSV file selected"}
                      </Typography>
                      {selectedFile && (
                        <Typography variant="caption" color="text.secondary">
                          {(selectedFile.size / 1024).toFixed(2)} KB
                        </Typography>
                      )}
                    </Box>
                  </Stack>

                  <Button variant="outlined" onClick={handleChooseFile}>
                    Choose CSV
                  </Button>
                </Paper>

                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="item rows allowed" size="small" color="primary" variant="outlined" />
                  <Chip label="variant rows allowed" size="small" color="secondary" variant="outlined" />
                  <Chip label="CSV only" size="small" variant="outlined" />
                </Stack>

                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Button
                    variant="outlined"
                    startIcon={<CloudUploadIcon />}
                    onClick={handleChooseFile}
                    disabled={!selectedLocationId}
                  >
                    Browse File
                  </Button>

                  <Button
                    variant="contained"
                    startIcon={
                      uploadingCsv ? (
                        <CircularProgress size={18} color="inherit" />
                      ) : (
                        <UploadFileIcon />
                      )
                    }
                    onClick={handleUploadCsv}
                    disabled={!selectedLocationId || !selectedFile || uploadingCsv}
                  >
                    {uploadingCsv ? "Uploading..." : "Upload CSV"}
                  </Button>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12}>
          <Card sx={{ borderRadius: 3 }}>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} mb={1.5}>
                Sample CSV Preview
              </Typography>

              <Typography variant="body2" color="text.secondary" mb={2}>
                Use this format while preparing the CSV. For items without variants,
                keep variant columns blank. For items with variants, keep item-level
                price fields blank and fill variant pricing fields.
              </Typography>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  overflow: "auto",
                  backgroundColor: "#0f172a",
                }}
              >
                <Typography
                  component="pre"
                  sx={{
                    m: 0,
                    color: "#e2e8f0",
                    fontFamily: "monospace",
                    fontSize: 12,
                    whiteSpace: "pre",
                    minWidth: 900,
                  }}
                >
                  {samplePreviewText}
                </Typography>
              </Paper>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default MenuCsvSection;